import { EFinancingProgram, EPaymentPlan } from '@if/core/types'
import { FinancingProgramProvider } from '@src/types/FinancingProgramProvider'
import { HomeKpi } from '@src/types/HomeKpi'
import { MechanicalWarrantyProduct } from '@src/types/MechanicalWarrantyProduct'
import { MerchantLoanActivityMetric, MerchantLoanActivityMetricsDto } from '@src/types/MerchantStatistic'
import { QueryFunctionContext, UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ResolvedPaymentPlan } from '@src/types/ResolvedPaymentPlan'
import { EReportGenerationFrequency, Merchant } from '../../../types/Merchant'
import { SearchMerchantFilters } from '../../../types/SearchMerchantFilters'
import { getApiClient } from '../api-client'

const SCOPE = 'merchants'
const LIST = 'list'
const DETAIL = 'detail'
const SCOPEMERCHANTS = 'merchants'
const SCOPEBANNERS = 'banners'
const SCOPEMECHANICALWARRANTY = 'mechanical-warranty-providers'
const SCOPEMECHANICALWARRANTYPRODUCTS = 'mechanical-warranty-providers-products'
const SCOPELOANACTIVITYMETRICS = 'loan-activity-metrics'
const SCOPEHOMEKPI = 'home-kpi'

const oneHour = 3600000

const keysFactory = {
  all: () => [{ scope: SCOPE }] as const,
  allLists: () => [{ scope: SCOPE, entity: LIST }] as const,
  list: (filters: SearchMerchantFilters) => [{ scope: SCOPE, entity: LIST, ...filters }] as const,
  allDetails: () => [{ scope: SCOPE, entity: DETAIL }] as const,
  detail: (id: string) => [{ scope: SCOPE, entity: DETAIL, id }] as const,
  bannerMerchants: (id: string) => [{ scope: SCOPEBANNERS, entity: SCOPEMERCHANTS, id }] as const,
  merchantLoanActivityMetrics: (merchantId: string, fromDate: string, toDate: string) =>
    [{ scope: SCOPE, entity: SCOPELOANACTIVITYMETRICS, merchantId, fromDate, toDate }] as const,
  homeKpi: () => [{ scope: SCOPE, entity: SCOPEHOMEKPI }] as const,
  mechanicalWarrantyProvider: (id: string, financingProgramId: string) =>
    [{ scope: SCOPE, entity: SCOPEMECHANICALWARRANTY, id, financingProgramId }] as const,
  mechanicalWarrantyProviderProducts: (id: string, providerId: string) =>
    [{ scope: SCOPE, entity: SCOPEMECHANICALWARRANTYPRODUCTS, id, providerId }] as const,
  resolvedPlan: (merchantId: string, paymentPlanId: string) =>
    [{ scope: SCOPE, entity: 'resolved-plan', merchantId, paymentPlanId }] as const,
  applicablePromoPlans: (merchantId: string) =>
    [{ scope: SCOPE, entity: 'applicable-promo-plans', merchantId }] as const,
}

/**
 * EasyCare is not offered on this dashboard, but the API still returns it in
 * `supportedFinancingPrograms` for merchants enrolled elsewhere. Strip it here so no
 * screen — program pickers, the new-application menu, resources — can surface it.
 */
const withoutUnsupportedPrograms = (merchant: Merchant): Merchant => ({
  ...merchant,
  supportedFinancingPrograms: (merchant.supportedFinancingPrograms ?? []).filter(
    (program) => program !== EFinancingProgram.EasyCareProtectionPlan,
  ),
})

const getMerchant = async ({
  queryKey: [{ id }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['detail']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<Merchant>(`/Merchant/${encodeURIComponent(id)}`)
  return withoutUnsupportedPrograms(response.data)
}

export function useMerchantByIdQuery(merchantId: string): UseQueryResult<Merchant> {
  return useQuery({
    queryKey: keysFactory.detail(merchantId),
    queryFn: getMerchant,
    enabled: !!merchantId,
    staleTime: oneHour,
    gcTime: oneHour,
  })
}

export function useMerchantById(merchantId: string): [Merchant | null, boolean] {
  const { data, isFetching } = useMerchantByIdQuery(merchantId)

  return [data ?? null, isFetching]
}

const getMerchantFinancingProgramProviders = async ({
  queryKey: [{ id, financingProgramId }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['mechanicalWarrantyProvider']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<FinancingProgramProvider[]>(
    `/Merchant/${encodeURIComponent(id)}/Providers/${encodeURIComponent(financingProgramId)}`,
  )
  return response.data
}

export function useGetMerchantFinancingProgramProviders(
  merchantId: string,
  financingProgramId: string,
): [FinancingProgramProvider[] | null, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.mechanicalWarrantyProvider(merchantId, financingProgramId),
    queryFn: getMerchantFinancingProgramProviders,
    enabled: !!merchantId && !!financingProgramId,
    staleTime: oneHour,
    gcTime: oneHour,
  })

  return [data ?? null, isFetching]
}

const getMerchantMechanicalWarrantyProviderProducts = async ({
  queryKey: [{ id, providerId }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['mechanicalWarrantyProviderProducts']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<MechanicalWarrantyProduct[]>(
    `/Merchant/${encodeURIComponent(id)}/mechwarranty/Providers/${encodeURIComponent(providerId)}/Products`,
  )
  return response.data
}

export function useGetMerchantMechanicalWarrantyProducts(
  merchantId: string,
  providerId: string,
): [MechanicalWarrantyProduct[] | null, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.mechanicalWarrantyProviderProducts(merchantId, providerId),
    queryFn: getMerchantMechanicalWarrantyProviderProducts,
    enabled: !!merchantId && !!providerId,
    staleTime: oneHour,
    gcTime: oneHour,
  })

  return [data ?? null, isFetching]
}

const getMerchantsByParentId = async ({
  queryKey: [{ id }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['bannerMerchants']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.post(`/Merchant/${id}/Subsidiaries`)
  return (response.data as Merchant[]).map(withoutUnsupportedPrograms)
}

export function useMerchantByParentIdQuery(id: string): UseQueryResult<Merchant[]> {
  return useQuery({
    queryKey: keysFactory.bannerMerchants(id),
    queryFn: getMerchantsByParentId,
    placeholderData: [],
    enabled: !!id,
    staleTime: oneHour,
    gcTime: oneHour,
  })
}

export function useMerchantByParentId(id: string): [Merchant[], boolean] {
  const { data, isFetching } = useMerchantByParentIdQuery(id)

  return [data!, isFetching]
}

const getMerchantLoanActivityMetrics = async ({
  queryKey: [{ merchantId, fromDate, toDate }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['merchantLoanActivityMetrics']>>) => {
  const apiClient = getApiClient()
  const dto: MerchantLoanActivityMetricsDto = {
    merchantId,
    fromDate,
    toDate,
  }
  const response = await apiClient.post<MerchantLoanActivityMetric[]>(
    `/Merchant/${encodeURIComponent(merchantId)}/MerchantLoanActivityMetrics/${EFinancingProgram.Personal}`,
    dto,
  )
  return response.data
}

export function useGetMerchantLoanActivityMetrics(
  merchantId: string,
  fromDate: string,
  toDate: string,
): [MerchantLoanActivityMetric[], boolean, number] {
  const { data, isFetching, dataUpdatedAt } = useQuery({
    queryKey: keysFactory.merchantLoanActivityMetrics(merchantId, fromDate, toDate),
    queryFn: getMerchantLoanActivityMetrics,
    placeholderData: [],
    enabled: !!merchantId && !!fromDate && !!toDate,
    staleTime: oneHour,
    gcTime: oneHour,
  })

  return [data!, isFetching, dataUpdatedAt]
}

const getHomeKpi = async () => {
  const apiClient = getApiClient()
  const response = await apiClient.get<HomeKpi>('/Merchant/HomeKpi')
  return response.data
}

export function useGetHomeKpi(): [HomeKpi | null, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.homeKpi(),
    queryFn: getHomeKpi,
    staleTime: oneHour,
    gcTime: oneHour,
  })
  return [data ?? null, isFetching]
}

export interface UpdateReportSettingsDto {
  reportGenerationFrequency: EReportGenerationFrequency
  reportRecipientsEmailList: string[]
}

export function useUpdateMerchantReportSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ merchantId, dto }: { merchantId: string; dto: UpdateReportSettingsDto }) => {
      const apiClient = getApiClient()
      const response = await apiClient.put<Merchant>(`/Merchant/${encodeURIComponent(merchantId)}/ReportSettings`, dto)
      return withoutUnsupportedPrograms(response.data)
    },
    onSuccess: (data, vars) => {
      queryClient.setQueryData(keysFactory.detail(vars.merchantId), data)
    },
  })
}

const getResolvedPaymentPlan = async ({
  queryKey: [{ merchantId, paymentPlanId }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['resolvedPlan']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<ResolvedPaymentPlan>(
    `/Merchant/${encodeURIComponent(merchantId)}/ResolvedPlan/${encodeURIComponent(paymentPlanId)}`,
  )
  return response.data
}

export function useResolvedPaymentPlan(
  merchantId: string | undefined,
  paymentPlanId: string | null | undefined,
): [ResolvedPaymentPlan | null, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.resolvedPlan(merchantId ?? '', paymentPlanId ?? ''),
    queryFn: getResolvedPaymentPlan,
    enabled: !!merchantId && !!paymentPlanId && paymentPlanId !== (EPaymentPlan.RegularDailyInterests as string),
    staleTime: oneHour,
    gcTime: oneHour,
  })

  return [data ?? null, isFetching]
}

const getApplicablePromoPlans = async ({
  queryKey: [{ merchantId }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['applicablePromoPlans']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<ResolvedPaymentPlan[]>(
    `/Merchant/${encodeURIComponent(merchantId)}/PromoPlans/Applicable`,
  )
  return response.data
}

export function useApplicablePromoPlans(merchantId: string | undefined): [ResolvedPaymentPlan[], boolean] {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.applicablePromoPlans(merchantId ?? ''),
    queryFn: getApplicablePromoPlans,
    enabled: !!merchantId,
    staleTime: oneHour,
    gcTime: oneHour,
  })

  return [data ?? [], isFetching]
}
